import React from 'react';

/**
 * KSButton — the standard KnightSteps 3D push-button.
 *
 * Props:
 *   children   — button label / content
 *   onClick    — click handler
 *   color      — tailwind color key used for bg/hover/border  (default: 'green')
 *                supported: 'green' | 'red' | 'yellow' | 'blue' | 'stone'
 *   textColor  — any valid CSS color string, e.g. '#fff', 'black', 'rgb(0,0,0)'  (default: 'white')
 *   width      — any valid CSS width string, e.g. '100%', '200px', 'auto'  (default: '100%')
 *   height     — any valid CSS height string, e.g. '56px', '48px', 'auto'  (default: '56px')
 *   disabled   — disables the button
 *   type       — button type attribute  (default: 'button')
 *   className  — extra tailwind classes appended to the outer button
 */

const COLOR_MAP = {
  green:   { bg: 'bg-green-600',   hover: 'hover:bg-green-500',   border: 'border-green-800'   },
  red:     { bg: 'bg-red-600',     hover: 'hover:bg-red-500',     border: 'border-red-800'     },
  yellow:  { bg: 'bg-amber-500',   hover: 'hover:bg-amber-400',   border: 'border-amber-700'   },
  amber:   { bg: 'bg-amber-500',   hover: 'hover:bg-amber-400',   border: 'border-amber-700'   },
  blue:    { bg: 'bg-blue-600',    hover: 'hover:bg-blue-500',    border: 'border-blue-800'    },
  stone:   { bg: 'bg-stone-600',   hover: 'hover:bg-stone-500',   border: 'border-stone-700'   },
  dark:    { bg: 'bg-zinc-900',    hover: 'hover:bg-zinc-800',    border: 'border-zinc-700', text: 'text-white' },
  purple:  { bg: 'bg-purple-600',  hover: 'hover:bg-purple-500',  border: 'border-purple-800'  },
  pink:    { bg: 'bg-pink-600',    hover: 'hover:bg-pink-500',    border: 'border-pink-800'    },
  cyan:    { bg: 'bg-cyan-600',    hover: 'hover:bg-cyan-500',    border: 'border-cyan-800'    },
  teal:    { bg: 'bg-teal-600',    hover: 'hover:bg-teal-500',    border: 'border-teal-800'    },
  emerald: { bg: 'bg-emerald-600', hover: 'hover:bg-emerald-500', border: 'border-emerald-800' },
  gray:    { bg: 'bg-slate-700',   hover: 'hover:bg-slate-600',   border: 'border-slate-900'   },
  indigo:  { bg: 'bg-indigo-600',  hover: 'hover:bg-indigo-500',  border: 'border-indigo-800'  },
  slate:   { bg: 'bg-slate-600',   hover: 'hover:bg-slate-500',   border: 'border-slate-800'   },
};

export default function KSButton({
  children,
  onClick,
  color = 'green',
  textColor = 'white',
  width = '100%',
  height,
  disabled = false,
  type = 'button',
  className = '',
  style = {},
  ...props
}) {
  const c = COLOR_MAP[color] ?? COLOR_MAP.green;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        ...(width ? { width } : {}),
        ...(height ? { height } : {}),
        color: textColor,
        ...style,
      }}
      className={[
        'inline-flex items-center justify-center gap-2',
        'px-4 py-2',
        'font-semibold text-sm',
        'rounded-lg',
        c.bg,
        c.hover,
        'border-b-4',
        c.border,
        'transition-all duration-150',
        'active:translate-y-[2px] active:brightness-95',
        disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer',
        className,
      ].join(' ')}
      {...props}
    >
      {children}
    </button>
  );
}

